import { useState, useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import type { AssetSource } from "@/types";

interface UseI2VImageReturn {
  imagePreview: string | null;
  imageS3Url: string | null;
  uploadId: string | null;
  inputGenerationId: string | null;
  /**
   * Character key carried in via nav state — preserved through the
   * generation submission so character-bound animations stay in the
   * character.
   */
  folder: string | null;
  isImageLoading: boolean;
  error: string | null;
  handleRemoveImage: () => void;
  handleAssetSelect: (
    url: string,
    assetId?: string,
    source?: AssetSource,
    folder?: string | null,
  ) => void;
  clearError: () => void;
}

export function useI2VImage(): UseI2VImageReturn {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageS3Url, setImageS3Url] = useState<string | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [inputGenerationId, setInputGenerationId] = useState<string | null>(
    null,
  );
  const [folder, setFolder] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasProcessedNavState = useRef(false);
  const hasProcessedSearchParams = useRef(false);

  // Handle image from URL search params (e.g., from cloak site redirect)
  useEffect(() => {
    const referenceImage = searchParams.get("reference_image");
    if (!referenceImage || hasProcessedSearchParams.current) return;

    hasProcessedSearchParams.current = true;
    setIsImageLoading(true);

    // Clear the reference_image param from URL to prevent re-processing
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("reference_image");
    setSearchParams(newParams, { replace: true });

    // Load the image from URL
    const img = new Image();
    img.onload = () => {
      setImagePreview(referenceImage);
      setImageS3Url(referenceImage);
      setUploadId(null);
      setInputGenerationId(null);
      setIsImageLoading(false);
    };
    img.onerror = () => {
      setIsImageLoading(false);
      setError("Failed to load image from URL");
    };
    img.src = referenceImage;
  }, [searchParams, setSearchParams]);

  // Handle image from navigation state (e.g., from "Animate Image")
  useEffect(() => {
    const state = location.state as {
      imageUrl?: string;
      uploadId?: string;
      imageId?: string; // Generation ID from "Animate Image" button
      folder?: string; // Character key carried from Character detail
    } | null;
    if (!state?.imageUrl) return;

    if (hasProcessedNavState.current) return;
    hasProcessedNavState.current = true;

    setIsImageLoading(true);
    setFolder(state.folder ?? null);
    window.history.replaceState({}, document.title);

    // If we have an imageId (generation ID), use input_generation_id
    if (state.imageId) {
      const img = new Image();
      img.onload = () => {
        setImagePreview(state.imageUrl!);
        setImageS3Url(state.imageUrl!);
        setInputGenerationId(state.imageId!);
        setUploadId(null);
        setIsImageLoading(false);
      };
      img.onerror = () => setIsImageLoading(false);
      img.src = state.imageUrl;
      return;
    }

    // If we already have an uploadId, the imageUrl is already an S3 URL
    if (state.uploadId) {
      const img = new Image();
      img.onload = () => {
        setImagePreview(state.imageUrl!);
        setImageS3Url(state.imageUrl!);
        setUploadId(state.uploadId!);
        setInputGenerationId(null);
        setIsImageLoading(false);
      };
      img.onerror = () => setIsImageLoading(false);
      img.src = state.imageUrl;
      return;
    }

    // Regular URL - just show preview (for existing asset URLs)
    const img = new Image();
    img.onload = () => {
      setImagePreview(state.imageUrl!);
      setImageS3Url(state.imageUrl!);
      setIsImageLoading(false);
    };
    img.onerror = () => setIsImageLoading(false);
    img.src = state.imageUrl;
  }, [location.state]);

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageS3Url(null);
    setUploadId(null);
    setInputGenerationId(null);
    setFolder(null);
    setError(null);
  };

  const handleAssetSelect = (
    url: string,
    assetId?: string,
    source?: AssetSource,
    folder?: string | null,
  ) => {
    setImagePreview(url);
    setImageS3Url(url);
    setError(null);
    // Carry the character key through — when the user picked a
    // character (or a character-bound asset) the resulting animation
    // should land in the same folder rather than as a loose video.
    // Explicit `null` clears any previously-selected folder.
    setFolder(folder ?? null);

    // Set the appropriate ID based on asset source
    if (source === "upload") {
      // For uploaded assets, use upload_id
      setUploadId(assetId || null);
      setInputGenerationId(null);
    } else {
      // For generated assets, use input_generation_id
      setInputGenerationId(assetId || null);
      setUploadId(null);
    }
  };

  return {
    imagePreview,
    imageS3Url,
    uploadId,
    inputGenerationId,
    folder,
    isImageLoading,
    error,
    handleRemoveImage,
    handleAssetSelect,
    clearError: () => setError(null),
  };
}
